<template>
  <td v-if="renderCell" class="groups-template" :class="className" >
    <div ref="tags-container">
      <rounded-group v-for="group in visibleTags" :key="group.id" :group="group" />
      <el-tooltip v-if="hiddenCount > 0" :open-delay="200">
        <div slot="content" class="max-w-sm max-h-500p overflow-auto">
          <template v-for="(item, index) in skippedValue">
            <label :key="index" class="text-sm dark:text-secondary-200">
              <base-icon v-if="item.Propietario" name="key" size="xs" />
              {{ item.Nombre }}
            </label>
            <span v-if="index < skippedValue.length - 1" :key="`separator.${index}`"> / </span>
          </template>
        </div>
        <span class="tooltip-icon__icon-wrap truncate">
          <rounded-tag :class="extraGroupClass" :tag="`+${hiddenCount}`" :format="format" :field="field" :extraparams="extraparams" />
        </span>
      </el-tooltip>
    </div>
  </td>
  <td v-else-if="renderArrow" :key="`g-${columnIndex}`" class="k-group-cell" />
  <td v-else-if="renderGroup" key="g-colspan" class="groups-template" :colSpan="columnsCount - columnIndex">
    <p class="k-reset">
      <a @click="$emit('click', $event, dataItem, expanded)" tabIndex="-1" href="#" :class="expanded ? 'k-i-collapse k-icon' : 'k-i-expand k-icon'" />
      {{ groupHeader }}
    </p>
  </td>
</template>
<script>
import getNestedValue from '@/services/functions/getNestedValue'
import getRoundedTagWidth from '@/services/functions/getRoundedTagWidth'
import { discard } from '@/services/functions/preserve&discard'
import generateHashFromObject from '@/services/functions/generateHashFromObject'
import RoundedGroup from '@/components/kendo/templates/RoundedGroup.vue'
import RoundedTag from '@/components/kendo/templates/RoundedTag.vue'
import NULL from '@/constants/NULL'

export default {
  name: 'groups-template',
  components: {
    RoundedGroup,
    RoundedTag
  },
  props: {
    tableId: [String, Number],
    field: String,
    dataItem: Object,
    format: String,
    className: String,
    columnIndex: Number,
    columnsCount: Number,
    rowType: String,
    level: Number,
    expanded: Boolean,
    editor: String,
    type: {
      type: String,
      default: 'Text'
    },
    curEditField: [String, null],
    editType: {
      type: [String, Boolean],
      default: 'row'
    },
    editField: {
      type: String,
      default: 'inEdit'
    },
    extraparams: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      visibleTags: [],
      hiddenCount: 0
    }
  },
  computed: {
    renderArrow () {
      var returnValue =
        this.columnIndex === undefined ||
        this.level === undefined ||
        this.columnIndex < this.level ||
        this.columnsCount === undefined ||
        this.rowType !== 'groupHeader' ||
        this.dataItem[this.field] === undefined
      // return returnValue && this.dataItem[this.field]
      return returnValue && this.dataItem[this.field] !== undefined
    },
    renderCell () {
      return this.field !== undefined && this.rowType !== 'groupHeader'
    },
    renderGroup () {
      return this.columnIndex <= this.level
    },
    value: function () {
      const value = getNestedValue(this.field, this.dataItem)
      return value === NULL ? null : value
    },
    groupHeader () {
      const value = this.value
      if (this.rowType === 'groupHeader') {
        const template = this.extraparams.groupHeaderTemplate
        if (typeof template === 'function') {
          return template(this.dataItem, { value, ...discard([template.name], this.extraparams) })
        }
      }
      return value
    },
    skippedValue () {
      if (this.hiddenCount === 0) {
        return []
      }
      return this.value.slice(-this.hiddenCount)
    },
    extraGroupClass () {
      var groupClass = 'group-border' // Gestión pro defecto
      if (this.skippedValue.some(x => x.idTipo === 1)) {
        groupClass = 'group-pool' // Gestor Facturas
      } else if (this.skippedValue.some(x => x.idTipo === 3)) {
        groupClass = 'group-reparto' // Reparto
      }
      return [groupClass, 'pb-0.5 pt-0.5']
    },
    computeHashFromDataGroup () {
      return generateHashFromObject(this.visibleTags)
    }
  },
  mounted: function () {
    this.visibleTags = this.value
    this.hiddenCount = 0
    this.$emit('register-template', this._uid, this.columnIndex, [{ name: 'columnResize', callbacks: [this.calculateVisibleTags] }])
    this.calculateVisibleTags()
  },
  beforeDestroy: function () {
    this.$emit('unregister-template', this._uid)
  },
  watch: {
    value: function (value, oldValue) {
      this.calculateVisibleTags()
    }
  },
  methods: {
    calculateVisibleTags (newWidth = undefined) {
      const container = this.$refs['tags-container']
      const tags = this.value
      if (!container || !tags.length) {
        this.visibleTags = []
        this.hiddenCount = 0
        return
      }

      const containerWidth = typeof newWidth === 'number' ? newWidth : container.clientWidth
      const visible = []
      var totalWidth = 0

      for (let i = 0; i < tags.length; i++) {
        let extraTagWidth = 0
        const tagValue = tags[i]
        const tagWidth = getRoundedTagWidth(tagValue.Nombre, true, { propietario: tagValue.Propietario })

        if (i < tags.length - 1) {
          const dummyValue = `+${tags.length - (visible.length + 1)}`
          extraTagWidth = getRoundedTagWidth(dummyValue)
        } else {
          extraTagWidth = 0
        }

        if (totalWidth + tagWidth + extraTagWidth > containerWidth) {
          break
        }

        totalWidth += tagWidth
        visible.push(tagValue)
      }

      if (this.visibleTags.length !== visible.length || this.computeHashFromDataGroup !== generateHashFromObject(visible)) {
        this.visibleTags = visible
      }
      if (this.hiddenCount !== tags.length - visible.length) {
        this.hiddenCount = tags.length - visible.length
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bg-success-p{
  background-color: black;
}
.k-grid {
  td.editable {
    text-overflow: clip;
    &::v-deep .el-input__inner {
      min-width: calc(30px + 1em);
    }
  }
}
.group-border{
  border-color: var(--Tag-border-)!important;
}
.group-pool{
  border-color: var(--primary-200)!important;
}
.group-reparto{
  border-color: var(--secondary-400)!important;
}
.dark {
  .group-border, .rounded-full{
    background-color: var(--darkblue);
  }
  .group-pool {
    background-color: var(--darkblue);
  }
  .group-reparto {
    background-color: var(--secondary-600);
  }
}
.rounded-full{
  margin-bottom: 0px;
}
</style>
